import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Button,
  Input,
  DatePicker,
  Card,
  List,
  Space,
  message,
  Typography,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Constants from "../Utils/Constants";


const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export default function ProgrammeModifier() {
  const { t } = useTranslation("programme", { useSuspense: false });
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const patientName = location.state?.patientName;

  const [programme, setProgramme] = useState(null);
  const [loading, setLoading] = useState(true);

  const [cyclesCatalog, setCyclesCatalog] = useState([]);
  const [loadingCycles, setLoadingCycles] = useState(true);

  const [selectedCycles, setSelectedCycles] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const today = dayjs().startOf("day");

  const getCycleEnd = (cycle) =>
    cycle?.endDate ? dayjs(cycle.endDate).startOf("day") : null;

  const isPastCycle = (cycle) => {
    const end = getCycleEnd(cycle);
    return Boolean(end && end.isBefore(today, "day"));
  };

  const isFutureCycle = (cycle) => {
    const start = cycle?.startDate ? dayjs(cycle.startDate).startOf("day") : null;
    return Boolean(start && start.isAfter(today, "day"));
  };

  // IMPORTANT :
  // On ne verrouille plus la date de début.
  // Donc la date de début reste modifiable même pour un cycle existant.
  const isCycleStartLocked = () => false;

  // Tu peux supprimer un nouveau cycle ou un cycle futur.
  // Les cycles déjà terminés restent protégés.
  const canRemoveCycle = (cycle) => cycle?.newCycle || isFutureCycle(cycle);

  // Bloque seulement les chevauchements avec les autres cycles.
  // On ne bloque plus automatiquement les dates passées.
  const isDateInExistingCycle = (currentIndex, date) => {
    if (!date) return false;

    const currentDay = date.startOf("day");

    return selectedCycles.some((cycle, index) => {
      if (index === currentIndex) return false;
      if (!cycle.startDate || !cycle.endDate) return false;

      const start = dayjs(cycle.startDate).startOf("day");
      const end = dayjs(cycle.endDate).startOf("day");

      return (
        currentDay.isSame(start, "day") ||
        currentDay.isSame(end, "day") ||
        (currentDay.isAfter(start, "day") && currentDay.isBefore(end, "day"))
      );
    });
  };

  const filteredCatalog = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return cyclesCatalog;

    return cyclesCatalog.filter((cycle) =>
      String(cycle?.name || "").toLowerCase().includes(query)
    );
  }, [cyclesCatalog, search]);

  useEffect(() => {
    const fetchProgramme = async () => {
      try {
        setLoading(true);

        const { data } = await api.get(
          `/programme/programmes/${encodeURIComponent(programmeId)}`
        );

        setProgramme(data);
      } catch (error) {
        console.error("Erreur GET /programme/programmes/:id :", error);
        message.error("Impossible de charger le programme.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgramme();
  }, [programmeId]);

  useEffect(() => {
    if (!programme?.cycles) return;

    const mappedCycles = programme.cycles.map((cycle, index) => ({
      id: cycle.id,
      weeklyCycleId: cycle.WeeklyCycleId,
      name: cycle.WeeklyCycle?.name || "Cycle sans nom",
      description: cycle.WeeklyCycle?.description || "",
      order: cycle.order ?? index + 1,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      newCycle: false,
    }));

    setSelectedCycles(mappedCycles);
  }, [programme]);

  useEffect(() => {
    const fetchCycles = async () => {
      try {
        setLoadingCycles(true);

        const { data } = await api.get("/cycles");

        setCyclesCatalog(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur GET /cycles :", error);
        message.error(
          t("label_error_loading_cycles") ||
            "Impossible de charger les cycles."
        );
        setCyclesCatalog([]);
      } finally {
        setLoadingCycles(false);
      }
    };

    fetchCycles();
  }, [t]);

  const handleAddCycle = (cycle) => {
    const alreadyAdded = selectedCycles.some(
      (selected) => selected.weeklyCycleId === cycle.id
    );

    if (alreadyAdded) {
      message.warning("Ce cycle est déjà dans le programme.");
      return;
    }

    setSelectedCycles((prev) => [
      ...prev,
      {
        weeklyCycleId: cycle.id,
        name: cycle.name,
        description: cycle.description || "",
        order: prev.length + 1,
        startDate: null,
        endDate: null,
        newCycle: true,
      },
    ]);
  };

  const handleDatesChange = (index, dates) => {
    const [start, end] = dates || [];

    setSelectedCycles((prev) =>
      prev.map((cycle, cycleIndex) => {
        if (cycleIndex !== index) return cycle;

        return {
          ...cycle,
          startDate: start ? start.format("YYYY-MM-DD") : null,
          endDate: end ? end.format("YYYY-MM-DD") : null,
        };
      })
    );
  };

  const handleRemoveCycle = (index) => {
    const cycle = selectedCycles[index];

    if (!canRemoveCycle(cycle)) {
      message.warning("Seuls les cycles futurs peuvent être supprimés.");
      return;
    }

    setSelectedCycles((prev) =>
      prev
        .filter((_, currentIndex) => currentIndex !== index)
        .map((currentCycle, orderIndex) => ({
          ...currentCycle,
          order: orderIndex + 1,
        }))
    );
  };

  const validateSelectedCycles = () => {
    if (selectedCycles.length === 0) {
      message.warning("Le programme doit contenir au moins un cycle.");
      return false;
    }

    const missingDates = selectedCycles.some(
      (cycle) => !cycle.startDate || !cycle.endDate
    );

    if (missingDates) {
      message.warning("Chaque cycle doit avoir une date de début et de fin.");
      return false;
    }

    const sortedCycles = [...selectedCycles].sort((a, b) =>
      dayjs(a.startDate).diff(dayjs(b.startDate))
    );

    for (let index = 1; index < sortedCycles.length; index += 1) {
      const previousEnd = dayjs(sortedCycles[index - 1].endDate).startOf("day");
      const currentStart = dayjs(sortedCycles[index].startDate).startOf("day");

      if (!currentStart.isAfter(previousEnd, "day")) {
        message.warning("Les périodes des cycles ne doivent pas se chevaucher.");
        return false;
      }
    }

    return true;
  };

  const handleSaveProgrammeModifications = async () => {
    if (!validateSelectedCycles()) return;

    const sortedCycles = [...selectedCycles].sort((a, b) =>
      dayjs(a.startDate).diff(dayjs(b.startDate))
    );

    const startDates = sortedCycles.map((cycle) => dayjs(cycle.startDate));
    const endDates = sortedCycles.map((cycle) => dayjs(cycle.endDate));

    const globalStart = startDates.reduce(
      (min, date) => (date.isBefore(min) ? date : min),
      startDates[0]
    );

    const globalEnd = endDates.reduce(
      (max, date) => (date.isAfter(max) ? date : max),
      endDates[0]
    );

    const durationWeeks = Math.max(
      1,
      Math.ceil(globalEnd.diff(globalStart, "day") / 7)
    );

    const payload = {
      name: programme.name,
      PatientId: programme.PatientId,
      startDate: globalStart.format("YYYY-MM-DD"),
      endDate: globalEnd.format("YYYY-MM-DD"),
      durationWeeks,
      timeOfDay: programme.timeOfDay || null,
      frequencyDays: programme.frequencyDays || [],
      status: programme.status || "ACTIVE",
      cycles: sortedCycles.map((cycle, index) => ({
        id: cycle.newCycle ? null : cycle.id,
        WeeklyCycleId: cycle.weeklyCycleId,
        order: index + 1,
        startDate: cycle.startDate,
        endDate: cycle.endDate,
      })),
    };

    try {
      setSaving(true);

      const { data } = await api.put(
        `/programme/programmes/${encodeURIComponent(programmeId)}`,
        payload
      );

      if (data?.sync?.status === "warning") {
        message.warning(
          data?.sync?.message ||
            "Programme modifié, mais la synchronisation mobile n’a pas pu être confirmée."
        );
      } else {
        message.success("Programme modifié avec succès.");
      }

      navigate(`/programme2/${programmeId}`, {
        replace: true,
        state: {
          patientName,
          showBackToList: true,
        },
      });
    } catch (error) {
      console.error("Erreur modification programme :", error);
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Erreur lors de la modification du programme."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelModification = () => {
    navigate("/programme2", { replace: true });
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Chargement du programme...</div>;
  }

  if (!programme) {
    return (
      <div style={{ padding: 24 }}>
        Programme introuvable.
        <br />
        <Button style={{ marginTop: 12 }} onClick={() => navigate("/programme2")}>
          Retour
        </Button>
      </div>
    );
  }

  const titlePatient = patientName || programme.name || `Programme #${programmeId}`;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleCancelModification}>
          Retour
        </Button>

        <Title level={3} style={{ margin: 0 }}>
          Modifier le programme : {titlePatient}
        </Title>
      </Space>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <Card
            title="Cycles du programme"
            extra={
              <Space>
                <Button onClick={handleCancelModification} disabled={saving}>
                  Annuler
                </Button>

                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveProgrammeModifications}
                  loading={saving}
                >
                  Enregistrer le programme
                </Button>
              </Space>
            }
          >
            <List
              dataSource={selectedCycles}
              locale={{
                emptyText: "Aucun cycle dans ce programme.",
              }}
              renderItem={(cycle, index) => {
                const disableStart = isCycleStartLocked(cycle);
                const disableEnd = isPastCycle(cycle);

                return (
                  <List.Item
                    actions={
                      canRemoveCycle(cycle)
                        ? [
                            <Button
                              danger
                              size="small"
                              onClick={() => handleRemoveCycle(index)}
                              key="remove"
                            >
                              Supprimer
                            </Button>,
                          ]
                        : []
                    }
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div>
                        <Text strong>
                          {index + 1}. {cycle?.name}
                        </Text>

                        {cycle?.description && (
                          <div style={{ fontSize: 12, color: "#888" }}>
                            {cycle.description}
                          </div>
                        )}
                      </div>

                      <RangePicker
                        format="DD/MM/YYYY"
                        allowClear
                        value={
                          cycle?.startDate && cycle?.endDate
                            ? [dayjs(cycle.startDate), dayjs(cycle.endDate)]
                            : null
                        }
                        onChange={(dates) => handleDatesChange(index, dates)}
                        disabled={[disableStart, disableEnd]}
                        disabledDate={(current) =>
                          isDateInExistingCycle(index, current)
                        }
                      />
                    </Space>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title="Catalogue de cycles"
            extra={
              <Input
                placeholder="Filtrer par nom de cycle"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
              />
            }
          >
            {loadingCycles ? (
              <p>Chargement...</p>
            ) : (
              <List
                dataSource={filteredCatalog}
                locale={{
                  emptyText: "Aucun cycle disponible.",
                }}
                renderItem={(cycle) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        onClick={() => handleAddCycle(cycle)}
                        key="add"
                      >
                        Ajouter
                      </Button>,
                    ]}
                  >
                    <div>
                      <Text strong>{cycle.name}</Text>

                      {cycle.description && (
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {cycle.description}
                        </div>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}